// QC-SPECIALIST-UI-PREMIUM-001 (Oct 2026) — presentational pieces for the QC Specialist
// dashboard. UI only: no QC routing, assignment, SLA/aging or RBAC logic lives here.
import React, { useEffect, useState } from 'react';
import { AlertTriangle, ChevronRight, Clock, CornerUpLeft, User } from 'lucide-react';

export const T = 'transition-[background-color,border-color,box-shadow,color,transform] duration-200 ease-out';

/* ── KPI card (acts as the queue tab) ────────────────────── */
export const KPI_TONES = {
  blue:    { pill: 'bg-blue-100 text-blue-700',    value: 'text-blue-700' },
  slate:   { pill: 'bg-slate-100 text-slate-600',  value: 'text-slate-800' },
  rose:    { pill: 'bg-rose-100 text-rose-600',    value: 'text-rose-600' },
  emerald: { pill: 'bg-emerald-100 text-emerald-600', value: 'text-emerald-600' },
  red:     { pill: 'bg-red-100 text-red-600',      value: 'text-red-600' },
};

export const KPICard = ({ icon: Icon, label, value, tone, isActive, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={isActive}
    data-testid={testId}
    data-active={isActive ? 'true' : 'false'}
    className={`group text-left bg-white rounded-xl pl-3 pr-2.5 py-3 flex items-center gap-3 border ${T}
      focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300
      ${isActive
        ? 'border-blue-300 ring-2 ring-blue-200/70 bg-blue-50/50 shadow-md'
        : 'border-slate-200 shadow-sm hover:shadow-md hover:border-slate-300 hover:-translate-y-px'}`}
  >
    <span className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 ${tone.pill}`}>
      {Icon ? <Icon className="h-[18px] w-[18px]" aria-hidden="true" /> : null}
    </span>
    <div className="min-w-0 flex-1">
      <p className={`text-[12px] font-medium leading-tight truncate ${isActive ? 'text-blue-700' : 'text-slate-500'}`}>{label}</p>
      <p className={`text-[22px] font-bold leading-tight tabular-nums ${tone.value}`}>{value}</p>
    </div>
    <ChevronRight className={`h-4 w-4 flex-shrink-0 ${T} ${isActive ? 'text-blue-500' : 'text-slate-300 group-hover:text-slate-400 group-hover:translate-x-0.5'}`} aria-hidden="true" />
  </button>
);

/* ── Aging pill — thresholds unchanged (2h warn, SLA breach) ── */
const normaliseUtc = (v) => (typeof v === 'string' ? (v.endsWith('Z') || v.includes('+') ? v : v + 'Z') : v);

export const AgingBadge = ({ submittedAt, slaThresholdHours = 4 }) => {
  const [elapsed, setElapsed] = useState({ text: '', hours: 0 });

  useEffect(() => {
    const calc = () => {
      if (!submittedAt) { setElapsed({ text: 'N/A', hours: 0 }); return; }
      const diffMs = Date.now() - new Date(normaliseUtc(submittedAt)).getTime();
      if (diffMs < 0) { setElapsed({ text: '0m', hours: 0 }); return; }
      const h = Math.floor(diffMs / 3600000);
      const m = Math.floor((diffMs % 3600000) / 60000);
      setElapsed({ text: h > 0 ? `${h}h ${m}m` : `${m}m`, hours: diffMs / 3600000 });
    };
    calc();
    const id = setInterval(calc, 60000);
    return () => clearInterval(id);
  }, [submittedAt]);

  const breached = elapsed.hours >= slaThresholdHours;
  const severity = breached ? 'breached' : elapsed.hours >= 2 ? 'warning' : 'normal';
  const cls = {
    normal: 'bg-slate-100 text-slate-600 border-slate-200',
    warning: 'bg-amber-50 text-amber-700 border-amber-200',
    breached: 'bg-rose-50 text-rose-700 border-rose-200',
  }[severity];

  return (
    <span className={`inline-flex items-center gap-1.5 h-7 px-2.5 text-[12px] font-semibold rounded-full border whitespace-nowrap tabular-nums ${cls}`}
      data-testid="aging-badge" data-severity={severity}>
      <Clock className="w-3.5 h-3.5" aria-hidden="true" />
      {elapsed.text}
      {breached && <AlertTriangle className="w-3.5 h-3.5" aria-hidden="true" />}
    </span>
  );
};

/* ── Return source (never guessed) ───────────────────────── */
const RETURN_SOURCE_LABELS = {
  typing: 'Typing', title_search: 'Title Search', tax_search: 'Tax Search',
  tax_processing: 'Tax Processing', search: 'Search', processing: 'Processing',
};
const titleCase = (s) => String(s).replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

export const getReturnSource = (o) => {
  const explicit = o?.qc_return_source || o?.qc_returned_from;
  if (explicit) return RETURN_SOURCE_LABELS[String(explicit).toLowerCase()] || titleCase(explicit);
  if (o?.typing_status === 'returned_to_qc') return 'Typing';
  return null;
};
export const isReturnedRow = (o) => !!(o?.typing_status === 'returned_to_qc' || o?.qc_return_source || o?.qc_returned_from);

export const ReturnedChip = ({ order }) => {
  if (!isReturnedRow(order)) return null;
  const source = getReturnSource(order);
  const testId = source === 'Typing' ? `qc-row-returned-from-typing-${order.id}` : `qc-row-returned-${order.id}`;
  return (
    <span
      className="inline-flex items-center gap-1 h-5 px-2 rounded-full bg-rose-50 border border-rose-200/80 text-rose-700 text-[11px] font-medium whitespace-nowrap"
      title={order.typing_return_reason || (source ? `Returned from ${source}` : 'Returned to QC')}
      data-testid={testId}
      data-return-source={source || ''}
    >
      <CornerUpLeft className="w-3 h-3" aria-hidden="true" />
      <span>Returned{source ? ` · ${source}` : ''}</span>
    </span>
  );
};

/* ── Assigned / Priority ─────────────────────────────────── */
const initialsOf = (name) => (name ? name.split(' ').filter(Boolean).map((n) => n[0]).join('').toUpperCase().slice(0, 2) : '?');

export const AssignedCell = ({ unassigned, assignedToMe, name }) => {
  if (unassigned) {
    return (
      <span className="inline-flex items-center gap-2 text-[13px] text-slate-500" data-testid="qc-assigned-unassigned">
        <span className="h-6 w-6 rounded-full border border-dashed border-slate-300 flex items-center justify-center text-slate-400"><User className="w-3 h-3" aria-hidden="true" /></span>
        Unassigned
      </span>
    );
  }
  const me = !!assignedToMe;
  return (
    <span className="inline-flex items-center gap-2 text-[13px] font-medium text-slate-700" title={name || ''} data-testid={me ? 'qc-assigned-me' : 'qc-assigned-other'}>
      <span className={`h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-bold ${me ? 'bg-blue-100 text-blue-700' : 'bg-amber-50 text-amber-700'}`}>
        {me ? 'ME' : initialsOf(name)}
      </span>
      <span className="truncate max-w-[110px]">{me ? 'Me' : (name?.split(' ')[0] || 'Other')}</span>
    </span>
  );
};

const PRIORITY_STYLE = {
  super_rush: { pill: 'bg-red-50 text-red-800 border-red-200', dot: 'bg-red-600' },
  rush:       { pill: 'bg-red-50 text-red-700 border-red-200', dot: 'bg-red-500' },
  high:       { pill: 'bg-orange-50 text-orange-700 border-orange-200', dot: 'bg-orange-500' },
  normal:     { pill: 'bg-slate-100 text-slate-600 border-slate-200', dot: 'bg-slate-400' },
  low:        { pill: 'bg-blue-50 text-blue-700 border-blue-200', dot: 'bg-blue-500' },
};

export const PriorityPill = ({ priority }) => {
  const key = priority?.toLowerCase() || 'normal';
  const s = PRIORITY_STYLE[key] || PRIORITY_STYLE.normal;
  return (
    <span className={`inline-flex items-center gap-1.5 h-7 px-2.5 text-[12px] font-medium rounded-full border whitespace-nowrap ${s.pill}`} data-testid="qc-priority-pill" data-priority={key}>
      <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} aria-hidden="true" />
      {titleCase(key)}
    </span>
  );
};

/* ── Compact icon action button ───────────────────────────── */
export const IconAction = ({ icon: Icon, label, tone = 'slate', onClick, disabled, testId }) => {
  const tones = {
    slate: 'text-slate-600 border-slate-200 hover:bg-slate-50 hover:border-slate-300',
    blue: 'text-blue-600 border-blue-200 hover:bg-blue-50 hover:border-blue-300',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      data-testid={testId}
      className={`h-8 w-8 min-w-[32px] flex-shrink-0 inline-flex items-center justify-center rounded-lg border bg-white shadow-sm ${T}
        hover:shadow hover:-translate-y-px disabled:opacity-50 disabled:pointer-events-none
        focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300 ${tones[tone]}`}
    >
      <Icon className="w-4 h-4" aria-hidden="true" />
    </button>
  );
};
